/**
/**
 * random.js - Utility functions for seeded random number generation
 */

/**
 * Generate a seed based on the current date for daily puzzles
 * @returns {number} A numerical seed derived from today's date
 */
export function generateDailySeed() { // Export function
  const today = new Date();

  // Format as YYYYMMDD to create a unique number for each day
  // Use UTC date components to avoid timezone issues affecting the seed
  const year = today.getUTCFullYear();
  const month = String(today.getUTCMonth() + 1).padStart(2, '0'); // Month is 0-indexed
  const day = String(today.getUTCDate()).padStart(2, '0');
  const dateString = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
  
  // Convert to a number and use it as a seed
  return Number(dateString);
};

/**
 * Creates a seeded pseudo-random number generator using the Mulberry32 algorithm.
 * @param {number} seed - The initial seed value. Should be an integer.
 * @returns {function(): number} A function that generates the next pseudo-random number between 0 (inclusive) and 1 (exclusive).
 */
export function createMulberry32Generator(seed) {
  let a = seed | 0; // Ensure seed is treated as a 32-bit integer
  return function() {
    a |= 0; // Ensure 'a' remains a 32-bit integer
    a = a + 0x6D2B79F5 | 0; // Increment 'a' and keep it 32-bit
    let t = Math.imul(a ^ a >>> 15, a | 1); // Perform multiplication safely for 32-bit
    t = t + Math.imul(t ^ t >>> 7, t | 61) | 0; // Add and keep 32-bit
    // Final XOR shift and division to get a float between 0 and 1
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  }
}

/**
 * Shuffle array using Fisher-Yates algorithm with a provided random function.
 * @template T
 * @param {Array<T>} array - The array to shuffle.
 * @param {function(): number} randomFunc - Function that returns a random number between 0 (inclusive) and 1 (exclusive).
 * @returns {Array<T>} A new array with the elements shuffled.
 */
export function shuffleArray(array, randomFunc) { // Export function
  if (!Array.isArray(array)) return [];
  const shuffled = [...array]; // Create a copy to avoid modifying the original
  for (let i = shuffled.length - 1; i > 0; i--) {
    // Generate a random index from 0 to i (inclusive)
    const j = Math.floor(randomFunc() * (i + 1));
    // Swap elements at indices i and j
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Pick a random element from an array using a provided random function.
 * @template T
 * @param {Array<T>} array - The array to pick from.
 * @param {function(): number} randomFunc - Function that returns a random number between 0 (inclusive) and 1 (exclusive).
 * @returns {T | undefined} A randomly selected element from the array, or undefined if the array is empty.
 */
export function randomChoice(array, randomFunc) { // Export function
  if (!Array.isArray(array) || array.length === 0) {
    return undefined;
  }
  const index = Math.floor(randomFunc() * array.length);
  return array[index];
}
